import { useQuery, QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeApi, KnowledgeItem } from '../services/api';
import { 
  knowledgeSignalRService, 
  KnowledgeDocumentCreatedEvent, 
  KnowledgeDocumentUpdatedEvent, 
  KnowledgeDocumentDeletedEvent,
  KnowledgeDocumentAccessRevokedEvent
} from '../services/knowledgeSignalRService';

const KNOWLEDGE_QUERY_KEY = ['knowledge-tree'];

const knowledgeQueryClient = new QueryClient();

export const useKnowledgeTree = () => {
  return useQuery({
    queryKey: KNOWLEDGE_QUERY_KEY,
    queryFn: () => knowledgeApi.getItems(),
    staleTime: 5 * 60 * 1000, 
    refetchOnWindowFocus: false,
  }, knowledgeQueryClient);
};

export const refreshKnowledgeTree = () => {
  knowledgeQueryClient.invalidateQueries({ queryKey: KNOWLEDGE_QUERY_KEY });
};

// ===== ВОССТАНОВЛЕННЫЕ ФУНКЦИИ ОПТИМИСТИЧНОГО ОБНОВЛЕНИЯ =====

/**
 * Добавляет новый документ в кэш дерева знаний
 */
export const addDocumentToKnowledgeCache = (newDocument: KnowledgeItem) => {
  knowledgeQueryClient.setQueryData(KNOWLEDGE_QUERY_KEY, (oldData: KnowledgeItem[] | undefined) => {
    if (!oldData) return [newDocument];
    
    // Проверяем, не существует ли уже документ с таким ID
    const existingIndex = oldData.findIndex(item => item.id === newDocument.id);
    if (existingIndex !== -1) {
      console.log(`[Cache] Документ ${newDocument.id} уже существует в кэше, обновляем`);
      const updatedData = [...oldData];
      updatedData[existingIndex] = newDocument;
      return updatedData;
    }
    
    console.log(`[Cache] Добавляем новый документ ${newDocument.id} в кэш`);
    return [...oldData, newDocument];
  });
};

/**
 * Удаляет документ из кэша дерева знаний
 */
export const removeDocumentFromKnowledgeCache = (documentId: string) => {
  knowledgeQueryClient.setQueryData(KNOWLEDGE_QUERY_KEY, (oldData: KnowledgeItem[] | undefined) => {
    if (!oldData) return [];
    
    console.log(`[Cache] Удаляем документ ${documentId} из кэша (рекурсивно)`);
    
    // Рекурсивная функция для удаления документа из любого уровня вложенности
    const removeRecursively = (items: KnowledgeItem[]): KnowledgeItem[] => {
      return items.map(item => {
        // Если это папка с детьми, рекурсивно обрабатываем детей
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: removeRecursively(item.children)
          };
        }
        return item;
      }).filter(item => item.id !== documentId); // Удаляем на текущем уровне
    };
    
    return removeRecursively(oldData);
  });
};

/**
 * Обновляет существующий документ в кэше дерева знаний
 */
export const updateDocumentInKnowledgeCache = (documentId: string, updates: Partial<KnowledgeItem>) => {
  knowledgeQueryClient.setQueryData(KNOWLEDGE_QUERY_KEY, (oldData: KnowledgeItem[] | undefined) => {
    if (!oldData) return [];
    
    const existingIndex = oldData.findIndex(item => item.id === documentId);
    if (existingIndex === -1) {
      console.warn(`[Cache] Документ ${documentId} не найден в кэше для обновления`);
      return oldData;
    }
    
    console.log(`[Cache] Обновляем документ ${documentId} в кэше`);
    const updatedData = [...oldData];
    updatedData[existingIndex] = { ...updatedData[existingIndex], ...updates };
    return updatedData;
  });
};

/**
 * Синхронизирует кэш с сервером при подключении
 * Проверяет, есть ли новые документы на сервере, которых нет в локальном кэше
 */
export const syncCacheWithServer = async () => {
  try {
    console.log('[Cache] Синхронизация кэша с сервером...');
    
    // Получаем текущие данные из кэша
    const cachedData = knowledgeQueryClient.getQueryData(KNOWLEDGE_QUERY_KEY) as KnowledgeItem[] | undefined;
    
    // Получаем актуальные данные с сервера
    const serverData = await knowledgeApi.getItems();
    
    if (!cachedData || cachedData.length === 0) {
      // Если кэш пуст, просто устанавливаем данные с сервера
      console.log('[Cache] Кэш пуст, устанавливаем данные с сервера');
      knowledgeQueryClient.setQueryData(KNOWLEDGE_QUERY_KEY, serverData);
      return;
    }
    
    // Создаем мапы для быстрого поиска
    const cachedIds = new Set(cachedData.map((item: KnowledgeItem) => item.id));
    const serverIds = new Set(serverData.map((item: KnowledgeItem) => item.id));
    
    // Ищем новые документы на сервере
    const newDocuments = serverData.filter((item: KnowledgeItem) => !cachedIds.has(item.id));
    
    // Ищем удаленные документы (есть в кэше, но нет на сервере)
    const deletedDocuments = cachedData.filter((item: KnowledgeItem) => !serverIds.has(item.id));
    
    // Ищем обновленные документы (только по метаданным, без сравнения контента)
    const updatedDocuments = serverData.filter((serverItem: KnowledgeItem) => {
      const cachedItem = cachedData.find((cached: KnowledgeItem) => cached.id === serverItem.id);
      if (!cachedItem) return false;
      
      const cachedUpdate = new Date(cachedItem.updatedAt || 0).getTime();
      const serverUpdate = new Date(serverItem.updatedAt || 0).getTime();
      
      return cachedItem.name !== serverItem.name || 
             cachedItem.parentId !== serverItem.parentId ||
             cachedUpdate < serverUpdate;
    });
    
    console.log(`[Cache] Синхронизация: ${newDocuments.length} новых, ${deletedDocuments.length} удаленных, ${updatedDocuments.length} обновленных`);
    
    // Применяем изменения
    if (newDocuments.length > 0 || deletedDocuments.length > 0 || updatedDocuments.length > 0) {
      let updatedCache = [...cachedData];
      
      // Удаляем удаленные документы
      deletedDocuments.forEach((doc: KnowledgeItem) => {
        updatedCache = updatedCache.filter((item: KnowledgeItem) => item.id !== doc.id);
      });
      
      // Добавляем новые документы
      updatedCache.push(...newDocuments);
      
      // Обновляем измененные документы
      updatedDocuments.forEach(serverDoc => {
        const index = updatedCache.findIndex(item => item.id === serverDoc.id);
        if (index !== -1) {
          updatedCache[index] = serverDoc;
        }
      });
      
      knowledgeQueryClient.setQueryData(KNOWLEDGE_QUERY_KEY, updatedCache);
      console.log('[Cache] Синхронизация завершена успешно');
    } else {
      console.log('[Cache] Синхронизация не требуется - кэш актуален');
    }
  } catch (error) {
    console.error('[Cache] Ошибка синхронизации кэша:', error);
    // При ошибке делаем полное обновление
    refreshKnowledgeTree();
  }
};

export const useKnowledgeSignalR = (shouldInitialize: boolean = true) => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Если инициализация не требуется, ничего не делаем
    if (!shouldInitialize) {
      console.log('[SignalR] Инициализация пропущена - пользователь не авторизован');
      return;
    }
    // ===== ОПТИМИСТИЧНЫЕ ОБРАБОТЧИКИ СОБЫТИЙ =====
    
    const handleDocumentCreated = (event: KnowledgeDocumentCreatedEvent) => {
      console.log('✅ [SignalR] Создан документ, добавляем в кэш:', event);
      
      const newDocument: KnowledgeItem = {
        id: event.id,
        name: event.name,
        parentId: event.parentId || null,
        itemType: event.itemType as "file" | "folder",
        fileType: event.fileType,
        content: event.content || '',
        createdAt: event.createdAt,
        updatedAt: event.createdAt
      };
      
      addDocumentToKnowledgeCache(newDocument);
    };
    
    const handleDocumentUpdated = (event: KnowledgeDocumentUpdatedEvent) => {
      console.log('✅ [SignalR] Обновлен документ другим участником:', event);
      
      // Проверяем, не является ли это обновлением текущего активного документа
      const currentPath = window.location.pathname;
      const isCurrentDocument = currentPath.includes(`/knowledge/${event.id}`);
      
      if (event.editorUsername && isCurrentDocument) {
        console.log(`📝 [SignalR] Документ ${event.id} редактируется пользователем ${event.editorUsername}. Обновляем открытый документ.`);
        
        // Обновляем кэш дерева файлов
        const treeUpdates: Partial<KnowledgeItem> = {
          updatedAt: event.updatedAt
        };
        
        if (event.name !== undefined) treeUpdates.name = event.name;
        if (event.content !== undefined) treeUpdates.content = event.content;
        
        updateDocumentInKnowledgeCache(event.id, treeUpdates);
        
        // Отправляем событие для обновления открытого документа
        const updateEvent = new CustomEvent('knowledgeDocumentUpdated', {
          detail: {
            documentId: event.id,
            updates: {
              name: event.name,
              content: event.content,
              updatedAt: event.updatedAt,
              editorUsername: event.editorUsername
            }
          }
        });
        
        window.dispatchEvent(updateEvent);
        
        console.log(`📄 [SignalR] Отправлено событие обновления для открытого документа ${event.id}`);
        return;
      }
      
      // Если это не текущий активный документ, обновляем только кэш дерева файлов
      const updates: Partial<KnowledgeItem> = {
        updatedAt: event.updatedAt
      };
      
      if (event.name !== undefined) updates.name = event.name;
      if (event.content !== undefined) updates.content = event.content;
      
      updateDocumentInKnowledgeCache(event.id, updates);
      
      console.log(`📄 [SignalR] Кэш документа ${event.id} обновлен без мерцания`);
    };
    
    const handleDocumentDeleted = (event: KnowledgeDocumentDeletedEvent) => {
      console.log('✅ [SignalR] Удален документ, удаляем из кэша:', event);
      removeDocumentFromKnowledgeCache(event.id);
      
      // Если удалённый документ сейчас открыт — уходим на /knowledge, иначе
      // эффекты страницы могут попытаться "восстановить" выбор/URL и получится прыжок.
      const currentPath = window.location.pathname;
      const isCurrentDocument = currentPath.includes(`/knowledge/${event.id}`);
      
      if (isCurrentDocument) {
        console.log(`🗑️ [SignalR] Текущий документ ${event.id} был удален, перенаправляем в базу знаний`);
        navigate('/knowledge', { replace: true });
      }
    };

    const handleDocumentAccessRevoked = (event: KnowledgeDocumentAccessRevokedEvent) => {
      console.log('✅ [SignalR] Доступ к документу отозван, тихо удаляем из базы знаний:', event);
      
      // НЕМЕДЛЕННО удаляем документ из кэша базы знаний
      removeDocumentFromKnowledgeCache(event.id);
      
      // Если это текущий открытый документ, перенаправляем пользователя БЕЗ уведомления
      const currentPath = window.location.pathname;
      const isCurrentDocument = currentPath.includes(`/knowledge/${event.id}`);
      
      if (isCurrentDocument) {
        console.log(`🚫 [SignalR] Текущий документ ${event.id} более недоступен, перенаправляем в базу знаний`);
        
        // SPA-навигация без полной перезагрузки страницы
        navigate('/knowledge', { replace: true });
      }
    };
    
    const handleTreeChanged = (event: any) => {
      console.log('✅ [SignalR] Общее изменение дерева, синхронизируем кэш:', event);
      // Для общих изменений делаем синхронизацию вместо полного обновления
      syncCacheWithServer();
    };
    
    // Этот обработчик будет вызываться при подключении и переподключении.
    const handleConnectionStatus = (connected: boolean) => {
      if (connected) {
        console.log('🔌✅ SignalR подключен. Синхронизируем кэш с сервером.');
        // При подключении синхронизируем кэш вместо полного обновления
        syncCacheWithServer();
      }
    };
    
    knowledgeSignalRService.subscribeToDocumentCreated(handleDocumentCreated);
    knowledgeSignalRService.subscribeToDocumentUpdated(handleDocumentUpdated);
    knowledgeSignalRService.subscribeToDocumentDeleted(handleDocumentDeleted);
    knowledgeSignalRService.subscribeToDocumentAccessRevoked(handleDocumentAccessRevoked);
    knowledgeSignalRService.subscribeToTreeChanged(handleTreeChanged);
    knowledgeSignalRService.subscribeToConnectionStatus(handleConnectionStatus);

    if (knowledgeSignalRService.isConnected) {
      // Если мы уже подключены, принудительно синхронизируем кэш на случай,
      // если компонент был перемонтирован.
      handleConnectionStatus(true);
    } else {
      // Иначе инициируем новое подключение.
      knowledgeSignalRService.connect();
    }

    return () => {
      knowledgeSignalRService.unsubscribeFromDocumentCreated(handleDocumentCreated);
      knowledgeSignalRService.unsubscribeFromDocumentUpdated(handleDocumentUpdated);
      knowledgeSignalRService.unsubscribeFromDocumentDeleted(handleDocumentDeleted);
      knowledgeSignalRService.unsubscribeFromDocumentAccessRevoked(handleDocumentAccessRevoked);
      knowledgeSignalRService.unsubscribeFromTreeChanged(handleTreeChanged);
      knowledgeSignalRService.unsubscribeFromConnectionStatus(handleConnectionStatus);
    };
  }, [shouldInitialize, navigate]);
}; 